import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ExternalLink, Search, Settings2, Store } from "lucide-react";
import {
  FRANCHISE_ADMIN_ROUTES,
  adminFranchiseListResponseSchema,
  franchiseSettingsResponseSchema,
  type AdminFranchiseItem,
  type AdminFranchisesQuery,
  type FranchiseSettings,
  type FranchiseSettingsInput,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Badge, Button, Input } from "@/shared/ui/core";
import { EmptyState, SkeletonTable } from "@/shared/ui/ui";
import { cn } from "@/shared/lib/cn";
import {
  AdminGuard,
  DataTable,
  FilterBar,
  Pagination,
  Td,
  Th,
  Tr,
  DEFAULT_PER_PAGE,
} from "@/features/admin/components";
import { formatOrderDate } from "@/features/admin/orders/format";

const NO_PREFIX = { baseUrl: "" } as const;

interface FranchisePage {
  items: AdminFranchiseItem[];
  total: number;
  page: number;
  perPage: number;
}

/** Búsqueda y paginación EN LA URL: el servidor devuelve solo la página. */
function listFranchises(query: AdminFranchisesQuery): Promise<FranchisePage> {
  const params = new URLSearchParams();
  params.set("page", String(query.page));
  params.set("perPage", String(query.perPage));
  if (query.query) params.set("query", query.query);
  return authFetch(
    `${FRANCHISE_ADMIN_ROUTES.list}?${params.toString()}`,
    adminFranchiseListResponseSchema,
    NO_PREFIX,
  );
}

async function getSettings(): Promise<FranchiseSettings> {
  const data = await authFetch(
    FRANCHISE_ADMIN_ROUTES.settings,
    franchiseSettingsResponseSchema,
    NO_PREFIX,
  );
  return data.settings;
}

async function saveSettings(input: FranchiseSettingsInput): Promise<FranchiseSettings> {
  const data = await authFetch(FRANCHISE_ADMIN_ROUTES.settings, franchiseSettingsResponseSchema, {
    ...NO_PREFIX,
    method: "PUT",
    body: input,
  });
  return data.settings;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Franquicias — el directorio Y su configuración en UNA card con
 * pestañas (Anahí, 27-ago: "agrega esto en un tab y quitamos
 * Configuración del sidebar"). La misma card de pestañas integradas
 * del Plan de comisiones.
 *
 * · Franquiciatarias: el directorio DERIVADO (quien tiene su orden de
 *   franquicia validada, con su tienda del módulo de tiendas).
 * · Configuración: lo que el checkout de franquicia COBRA desde que se
 *   guarda — lo ya vendido no cambia.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminFranchisesPage() {
  return (
    <AdminGuard>
      <FranchisesScreen />
    </AdminGuard>
  );
}

const TABS = [
  { key: "directorio" as const, label: "Franquiciatarias", icon: Store },
  { key: "config" as const, label: "Configuración", icon: Settings2 },
];

function FranchisesScreen() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<(typeof TABS)[number]["key"]>("directorio");

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Franquicias")}
        description={t("Quiénes ya son franquiciatarias, y lo que cuesta serlo.")}
      />

      <div className="overflow-hidden rounded-3xl border border-ds-border bg-white shadow-ds-float">
        {/* ── Las pestañas, integradas al borde superior ──────────────── */}
        <div
          role="tablist"
          aria-label={t("Franquicias")}
          className="flex flex-wrap border-b border-ds-border bg-ds-cream-2/50"
        >
          {TABS.map((item) => (
            <button
              key={item.key}
              role="tab"
              aria-selected={tab === item.key}
              onClick={() => setTab(item.key)}
              className={cn(
                "relative inline-flex items-center gap-2 px-5 py-3.5 text-[13px] font-semibold transition",
                tab === item.key
                  ? "bg-white text-ds-violet-pop after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-ds-violet-pop"
                  : "text-ds-ink-3 hover:bg-white/60 hover:text-ds-ink",
              )}
            >
              <item.icon className="h-4 w-4" />
              {t(item.label)}
            </button>
          ))}
        </div>

        {tab === "directorio" ? <DirectoryTab /> : <SettingsTab />}
      </div>
    </>
  );
}

// ─── El directorio ───────────────────────────────────────────────────


function DirectoryTab() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  /*
   * Las filas por página las elige quien mira (Anahí, 29-ago): 5, 10 o 20,
   * con CINCO por defecto. Vive en el estado y no como constante porque
   * ahora cambia en caliente; al cambiarlo se vuelve a la página 1, que es
   * lo único sensato — la página 4 de 5 en 5 no es la página 4 de 20 en 20.
   */
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
  const [search, setSearch] = useState("");

  // Búsqueda y paginación de SERVIDOR: solo viaja la página visible.
  const query: AdminFranchisesQuery = {
    page,
    perPage: perPage,
    ...(search.trim() ? { query: search.trim() } : {}),
  };
  const franchises = useQuery({
    queryKey: ["admin", "franchises", query],
    queryFn: () => listFranchises(query),
    placeholderData: (previous) => previous,
  });
  const items = franchises.data?.items ?? [];
  const total = franchises.data?.total ?? 0;

  const filterBar = (
    <FilterBar>
      <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
        {t("Buscar")}
        <div className="relative w-64">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-ink-3" />
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder={t("Nombre o correo…")}
            className="pl-10"
          />
        </div>
      </label>
    </FilterBar>
  );

  if (franchises.isPending) {
    return (
      <>
        {filterBar}
        <div className="p-5">
          <SkeletonTable rows={4} columns={5} />
        </div>
      </>
    );
  }

  if (items.length === 0) {
    return (
      <>
        {filterBar}
        <div className="p-4">
          <EmptyState
            icon={<Store className="h-5 w-5 text-ds-ink-3" />}
            title={search ? t("Sin resultados") : t("Todavía no hay franquicias")}
            description={
              search
                ? t("Nadie coincide con esa búsqueda.")
                : t("Cuando valides una orden de franquicia, su dueña aparece aquí.")
            }
          />
        </div>
      </>
    );
  }

  return (
    <>
    {filterBar}
    <DataTable caption={t("Franquiciatarias")}>
      <thead>
        <tr>
          <Th>{t("Franquiciataria")}</Th>
          <Th>{t("Teléfono")}</Th>
          <Th>{t("Tienda")}</Th>
          <Th>{t("Estado")}</Th>
          <Th align="right">{t("Desde")}</Th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <Tr key={item.userId}>
            <Td>
              <p className="font-medium">{item.name}</p>
              <p className="text-[12px] text-ds-ink-3">{item.email}</p>
            </Td>
            <Td className="text-ds-ink-3">{item.phone ?? "—"}</Td>
            <Td>
              {item.storeSlug ? (
                <a
                  href={`/shop/t/${item.storeSlug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ds-violet-pop hover:underline"
                >
                  {item.storeName ?? item.storeSlug}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : (
                <span className="text-[13px] text-ds-ink-3">{t("Sin abrir")}</span>
              )}
            </Td>
            <Td>
              <Badge variant="success" size="sm" dot>
                {t("Activa")}
              </Badge>
            </Td>
            <Td align="right" className="text-ds-ink-3">
              {formatOrderDate(item.franchiseeSince)}
            </Td>
          </Tr>
        ))}
      </tbody>
    </DataTable>

    {total > 0 && (
      <Pagination page={page} perPage={perPage} total={total} onPageChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
    )}
    </>
  );
}

// ─── La configuración ────────────────────────────────────────────────

function SettingsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ["admin", "franchise-settings"],
    queryFn: getSettings,
  });

  const [form, setForm] = useState<{
    priceMXN: string;
    priceUSD: string;
    months: string;
  } | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data && !form) {
      setForm({
        priceMXN: String(settings.data.priceMXNCents / 100),
        priceUSD: String(settings.data.priceUSDCents / 100),
        months: String(settings.data.durationMonths),
      });
    }
  }, [settings.data, form]);

  const guardar = useMutation({
    mutationFn: saveSettings,
    onSuccess: (fresh) => {
      queryClient.setQueryData(["admin", "franchise-settings"], fresh);
      setSaved(true);
    },
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo guardar")),
  });

  function enviar(event: FormEvent) {
    event.preventDefault();
    if (!form) return;
    setError(null);
    setSaved(false);

    const priceMXNCents = Math.round(Number(form.priceMXN) * 100);
    const priceUSDCents = Math.round(Number(form.priceUSD) * 100);
    const durationMonths = Number(form.months);
    if (
      [priceMXNCents, priceUSDCents].some((cents) => !Number.isFinite(cents) || cents < 0) ||
      !Number.isInteger(durationMonths) ||
      durationMonths < 1
    ) {
      setError(t("Revisa los números: precios en pesos/dólares, vigencia en meses"));
      return;
    }

    guardar.mutate({ priceMXNCents, priceUSDCents, durationMonths });
  }

  if (settings.isPending || !form) {
    return (
      <div className="p-5">
        <SkeletonTable rows={2} columns={2} />
      </div>
    );
  }

  return (
    <form onSubmit={enviar} className="grid gap-5 p-6">
      <p className="text-[13px] text-ds-ink-3">
        {t("La página pública de franquicias, el botón de pago y el checkout usan estos números desde que guardes. Lo ya vendido no cambia.")}
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t("Precio (MXN)")}
          type="number"
          min={0}
          step="0.01"
          value={form.priceMXN}
          onChange={(event) => setForm({ ...form, priceMXN: event.target.value })}
          hint={t("Lo que paga quien adquiere en pesos.")}
        />
        <Input
          label={t("Precio (USD)")}
          type="number"
          min={0}
          step="0.01"
          value={form.priceUSD}
          onChange={(event) => setForm({ ...form, priceUSD: event.target.value })}
          hint={t("Lo que paga quien adquiere en dólares.")}
        />
        <Input
          label={t("Vigencia (meses)")}
          type="number"
          min={1}
          max={60}
          value={form.months}
          onChange={(event) => setForm({ ...form, months: event.target.value })}
          hint={t("Lo que la franquicia dura por cada pago.")}
        />
      </div>

      {error && (
        <Alert variant="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      {saved && (
        <Alert variant="success" onDismiss={() => setSaved(false)}>
          {t("Guardado. El checkout de franquicia ya cobra estos precios.")}
        </Alert>
      )}

      <div className="flex justify-end">
        <Button type="submit" loading={guardar.isPending}>
          {t("Guardar cambios")}
        </Button>
      </div>
    </form>
  );
}
